import React, {useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faInfoCircle} from '@fortawesome/fontawesome-pro-solid';

import {ToolTipCell} from 'components/ToolTipCell';
import MultiSelect from 'components/MultiSelect';
import {getFieldNameKey, extractDynamicFieldOptions} from './utils';
import DynamicFilter from './dynamic';
import {getSelectedFiltersFromUrl} from './url';

/** TODO: Remove legacy filters and logic like selectedStates and selectedPlanTypes */
export const CoverageFilters = ({
  states,
  planTypes,
  selectedStates,
  selectedPlanTypes,
  selectedDynamicFilters,
  coveragePositions,
  inProgress,
  settings,
  styleSheet,
  urlParameters,
  onUrlFiltersUpdate,
  onFilterChange,
}) => {
  // Track previous filter data to detect changes
  const prevFilterDataRef = useRef({
    states,
    planTypes,
    coveragePositions,
  });

  // Effect to handle default selections and URL parameter restoration when filter data loads
  useEffect(() => {
    const prevFilterData = prevFilterDataRef.current;
    const filtersChanged = hasFilterDataChanged(prevFilterData, {states, planTypes, coveragePositions});

    // Apply defaultSelected values for dynamic filters that have no existing selection
    if (filtersChanged && coveragePositions?.length > 0) {
      const defaults = getDefaultSelectedFilters(coveragePositions, settings.filterLayout, selectedDynamicFilters);
      if (Object.keys(defaults).length > 0 && onFilterChange) {
        onFilterChange({selectedDynamicFilters: {...(selectedDynamicFilters || {}), ...defaults}});
      }
    }

    // Skip URL restoration if filter data hasn't changed or no URL parameters are present
    if (!filtersChanged || !urlParameters || Object.keys(urlParameters).length === 0) {
      // Update ref for next comparison
      prevFilterDataRef.current = {
        states,
        planTypes,
        coveragePositions,
      };
      return;
    }

    const urlParsingProps = {states, usStates: states, planTypes, settings};
    const urlFilterTypes = getUrlFilterTypes(urlParameters, settings);

    // Restore filter selections from URL once all required filter data has loaded
    if (checkAllFiltersLoaded(urlFilterTypes, settings.filterLayout, urlParsingProps, coveragePositions) && onUrlFiltersUpdate) {
      const selectedFilters = getSelectedFiltersFromUrl(urlParameters, settings.filterLayout, urlParsingProps, coveragePositions);
      if (Object.keys(selectedFilters).length > 0) {
        onUrlFiltersUpdate(selectedFilters);
      }
    }

    prevFilterDataRef.current = {
      states,
      planTypes,
      coveragePositions,
    };
  }, [states, planTypes, coveragePositions, urlParameters, settings, onUrlFiltersUpdate, selectedDynamicFilters, onFilterChange]);

  // Internal handleChange function that calls onFilterChange callback
  const handleFilterChange = (selected, type, singleSelect) => {
    // eslint-disable-next-line
    switch (type) {
      case 'states':
        onFilterChange({selectedStates: selected});
        break;
      case 'planTypes':
        onFilterChange({selectedPlanTypes: selected});
        break;
      default:
        break;
    }
  };

  const multiSelectFilter = (column, options, selected, type, additionalProps = {}) => {
    // Create a wrapper function that captures singleSelect from column
    const createHandleChange = (type) => (selected) => {
      handleFilterChange(selected, type, column.singleSelect);
    };
    // Normalize selected so each has valid label for MultiSelect (API may use 'name' instead of 'label')
    const normalizedSelected = (selected || []).filter(Boolean).map((s) => ({
      ...s,
      label: s.label ?? s.name ?? String(s.id ?? ''),
    }));
    return (
      <Col key={column.filter} sm={column.gridWidth} className={`offset-sm-${column.offset}`}>
        <MultiSelect
          label={additionalProps.label || column.label}
          placeholder={column.placeholder}
          options={options}
          selected={normalizedSelected}
          isFetching={inProgress}
          handleChange={createHandleChange(type)}
          selectHintOnEnter={false}
          type={type}
          {...additionalProps}
        />
      </Col>
    );
  };

  return (
    <Row as="section" aria-label="Filters" key="filterInputs">
      {settings.filterLayout.map((column) => {
        // Handle dynamic filter type using DynamicFilter component
        if (column.filter === 'dynamic' && coveragePositions) {
          const fieldName = column.fieldName;
          return (
            <DynamicFilter
              key={`dynamic-${getFieldNameKey(fieldName)}`}
              column={column}
              coveragePositions={coveragePositions}
              selectedDynamicFilters={selectedDynamicFilters}
              onFilterChange={onFilterChange}
              inProgress={inProgress}
              settings={settings}
              styleSheet={styleSheet}
            />
          );
        }

        switch (column.filter) {
          case 'state': {
            // Ensure each state has valid label for MultiSelect (API may use 'name' or 'label')
            const stateOptions = (states || []).map((s) => ({
              ...s,
              label: s.label ?? s.name ?? String(s.id ?? ''),
            }));
            return multiSelectFilter(column, stateOptions, selectedStates, 'states');
          }
          case 'plan': {
            const displayedPlanTypes = settings.displayedPlanTypes.length
              ? settings.displayedPlanTypes.map((planType) => {
                const base = {id: planType.ids.join(','), ...planType};
                // Ensure label exists for MultiSelect (API may use 'name' instead of 'label')
                base.label = base.label ?? base.name ?? String(base.id ?? '');
                return base;
              })
              : (planTypes || []).map((pt) => {
                const base = {...pt};
                base.label = base.label ?? base.name ?? String(base.id ?? '');
                return base;
              });
            // labels with hover are rendered as accessible tooltips
            // otherwise labels are just plain text
            const hasHelpHover = settings.content?.planTypeHelpHover?.length > 0;
            const planTypeDescriptionId = 'plantype-description';
            const planLabel = hasHelpHover
              ? <span>{column.label}
                <ToolTipCell
                  text={settings.content.planTypeHelpHover}
                  placeholder={(<FontAwesomeIcon icon={faInfoCircle}/>)}
                  placement={'bottom'}
                  styleSheet={styleSheet}
                  id={'plantype-tooltip'}
                  ariaLabel={'More information about plan types'}
                />
                <span id={planTypeDescriptionId} className="sr-only visually-hidden">
                  {settings.content.planTypeHelpHover}
                </span>
              </span>
              : column.label;
            return multiSelectFilter(
              column,
              displayedPlanTypes,
              selectedPlanTypes,
              'planTypes',
              {
                label: planLabel,
                hasChildren: column.hasChildren,
                dataTestId: 'plan-type-multiselect',
                ...(hasHelpHover && {
                  typeAheadProps: {inputProps: {'aria-describedby': planTypeDescriptionId}},
                }),
              }
            );
          }
          default:
            return null;
        }
      })}
    </Row>
  );
};

CoverageFilters.propTypes = {
  states: PropTypes.array,
  planTypes: PropTypes.array,
  selectedStates: PropTypes.array.isRequired,
  selectedPlanTypes: PropTypes.array.isRequired,
  selectedDynamicFilters: PropTypes.object,
  coveragePositions: PropTypes.array,
  inProgress: PropTypes.bool.isRequired,
  settings: PropTypes.object.isRequired,
  styleSheet: PropTypes.object.isRequired,
  urlParameters: PropTypes.object,
  onUrlFiltersUpdate: PropTypes.func,
  onFilterChange: PropTypes.func.isRequired,
};

const mapStateToProps = function (state) {
  const {dynamicCoverage} = state;

  const result = {
    states: dynamicCoverage.get('states').toJS(),
    planTypes: dynamicCoverage.get('planTypes').toJS(),
    coveragePositions: dynamicCoverage.get('coveragePositions').toJS(),
  };

  return result;
};

export default connect(mapStateToProps)(CoverageFilters);


const hasFilterDataChanged = (prev, next) =>
  JSON.stringify(next.states) !== JSON.stringify(prev.states) ||
  JSON.stringify(next.planTypes) !== JSON.stringify(prev.planTypes) ||
  JSON.stringify(next.coveragePositions) !== JSON.stringify(prev.coveragePositions);

// Builds default selections for dynamic filters that define `defaultSelected` (array of option labels).
// Only applies when the filter has no existing selection (e.g. on initial load, not after user interaction).
const getDefaultSelectedFilters = (coveragePositions, filterLayout, selectedDynamicFilters) => {
  const defaults = {};
  for (const col of filterLayout) {
    if (col.filter === 'dynamic' && col.defaultSelected?.length > 0) {
      const key = getFieldNameKey(col.fieldName);
      if (!selectedDynamicFilters?.[key]?.length) {
        const allOptions = extractDynamicFieldOptions(coveragePositions, col.fieldName, filterLayout);
        defaults[key] = allOptions.filter((opt) => col.defaultSelected.includes(opt.label));
      }
    }
  }
  return defaults;
};

const getUrlFilterTypes = (urlParameters, settings) => {
  const hasDynamicFilters = settings.filterLayout && settings.filterLayout.some((f) => f.filter === 'dynamic');
  const urlFilterTypes = new Set();
  if (urlParameters && typeof urlParameters === 'object') {

    for (let filter of settings.filterLayout) {
      if (filter.filter !== 'dynamic') {
        const filterAlias = settings.urlParametersMap[filter.filter]?.alias || filter.filter;
        if (Object.prototype.hasOwnProperty.call(urlParameters, filterAlias)) {
          urlFilterTypes.add(filter.filter);
        }
      }
    }
    if (hasDynamicFilters) {
      for (let filter of settings.filterLayout) {
        if (filter.filter === 'dynamic') {
          const fieldName = filter.fieldName;
          const fieldNameKey = getFieldNameKey(fieldName);
          const possibleKeys = [
            settings.urlParametersMap[fieldNameKey]?.alias || fieldNameKey,
            fieldNameKey,
            fieldName,
            settings.urlParametersMap[fieldName]?.alias || fieldName,
          ];
          if (possibleKeys.some((key) => Object.prototype.hasOwnProperty.call(urlParameters, key))) {
            urlFilterTypes.add('dynamic');
            break;
          }
        }
      }
    }
  }
  return urlFilterTypes;
};

const checkAllFiltersLoaded = (urlFilterTypes, filterLayout, urlParsingProps, coveragePositions) => {
  let allFiltersLoaded = true;
  for (let filter of filterLayout) {
    if (filter.filter !== 'dynamic' && urlFilterTypes.has(filter.filter)) {
      const filterPropString = (filter.filter === 'state')
        ? 'states'
        : (filter.filter === 'plan')
          ? 'planTypes'
          : `${filter.filter}s`;
      const filterData = urlParsingProps[filterPropString];
      if (!filterData || !Array.isArray(filterData) || filterData.length === 0) {
        allFiltersLoaded = false;
        break;
      }
    }
  }
  if (urlFilterTypes.has('dynamic') && (!coveragePositions || !Array.isArray(coveragePositions) || coveragePositions.length === 0)) {
    allFiltersLoaded = false;
  }
  if (urlFilterTypes.size === 0) {
    allFiltersLoaded = false;
  }
  return allFiltersLoaded;
};

